/**
 * NeuralAvatarStream
 * ==================
 * Three-state avatar for the AI Agent call hub:
 *
 *   inactive  (active=false)
 *     → AIAvatar SVG in STANDBY mode — zero network activity.
 *
 *   active + quiet  (active=true, agentTalking=false)
 *     → Loops the pre-generated MuseTalk idle preview MP4 from
 *       /api/v1/avatar/preview-video.  Falls back to AIAvatar when
 *       no video has been generated yet (404 from the endpoint).
 *
 *   active + talking  (active=true, agentTalking=true)
 *     → NeuralAvatar canvas: connects to /ws/neural-avatar and renders
 *       live Wav2Lip JPEG frames at 25 fps.  Falls back to AIAvatar
 *       (with agentTalking=true) when Wav2Lip is unavailable.
 *
 * Ref handle (NeuralAvatarHandle) is forwarded so the parent can call
 * sendText() / sendPcm() to push audio/text to the backend Wav2Lip service.
 *
 * No MediaPipe readiness gate — the component mounts immediately when
 * `active` becomes true.
 */

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Box } from '@mui/material';
import { API_BASE_URL } from '../../config/api';

import { AIAvatar }      from './AIAvatar';
import { NeuralAvatar }  from './NeuralAvatar';
import type { NeuralAvatarHandle, NeuralAvatarProps } from './NeuralAvatar';

// ─── Constants ────────────────────────────────────────────────────────────────

const PREVIEW_VIDEO_URL = `${API_BASE_URL}/avatar/preview-video`;

// ─── Component ────────────────────────────────────────────────────────────────

export const NeuralAvatarStream = forwardRef<NeuralAvatarHandle, NeuralAvatarProps>(
  ({ active, width, height, agentTalking = false }, ref) => {

    // Whether the idle preview MP4 is available (404 → fall back to SVG)
    const [hasIdleVideo, setHasIdleVideo] = useState<boolean | null>(null); // null = unchecked

    // Imperative handle — delegates to whichever child is active
    const neuralRef = useRef<NeuralAvatarHandle>(null);
    const aiRef     = useRef<NeuralAvatarHandle>(null);

    useImperativeHandle(ref, () => ({
      sendPcm(pcm: ArrayBuffer) {
        neuralRef.current?.sendPcm(pcm);
        aiRef.current?.sendPcm(pcm);
      },
      sendText(text: string) {
        neuralRef.current?.sendText(text);
        aiRef.current?.sendText(text);
      },
      getAudioStream(): MediaStream | null {
        return neuralRef.current?.getAudioStream() ?? aiRef.current?.getAudioStream() ?? null;
      },
    }));

    // Probe the preview-video endpoint once when we become active
    useEffect(() => {
      if (!active || hasIdleVideo !== null) return;
      fetch(PREVIEW_VIDEO_URL, { method: 'HEAD' })
        .then(r => setHasIdleVideo(r.ok))
        .catch(() => setHasIdleVideo(false));
    }, [active, hasIdleVideo]);

    // Reset probe when deactivated so a newly generated video is detected next time
    useEffect(() => {
      if (!active) setHasIdleVideo(null);
    }, [active]);

    // ── Inactive: SVG standby ─────────────────────────────────────────────────
    if (!active) {
      return (
        <AIAvatar
          ref={aiRef}
          active={false}
          width={width}
          height={height}
          agentTalking={false}
        />
      );
    }

    // ── Active + talking: live Wav2Lip canvas ─────────────────────────────────
    if (agentTalking) {
      return (
        <NeuralAvatar
          ref={neuralRef}
          active={true}
          width={width}
          height={height}
          agentTalking={true}
        />
      );
    }

    // ── Active + quiet: idle MP4 loop (or fallback SVG) ───────────────────────
    if (hasIdleVideo) {
      return (
        <Box
          sx={{
            width,
            height,
            bgcolor: '#0a0a18',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <Box
            component="video"
            src={`${PREVIEW_VIDEO_URL}?cb=${Math.floor(Date.now() / 60000)}`}
            autoPlay
            loop
            muted
            playsInline
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </Box>
      );
    }

    // Fallback: animated SVG (no video generated yet, or video fetch failed)
    return (
      <AIAvatar
        ref={aiRef}
        active={true}
        width={width}
        height={height}
        agentTalking={false}
      />
    );
  },
);

NeuralAvatarStream.displayName = 'NeuralAvatarStream';
